import React from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { Crown, Save, Gamepad2 } from 'lucide-react';
import './GameLimitIndicator.css';

function GameLimitIndicator({ compact = false, showPlayLimit = false }) {
  const { 
    subscription, 
    isPremium, 
    savedGamesCount, 
    maxSavedGames, 
    savesRemaining,
    playedGamesCount,
    maxPlayedGames,
    playsRemaining
  } = useSubscription();

  // Don't show for premium users
  if (isPremium || subscription.loading) {
    return null;
  }

  const savePercentage = Math.min(100, (savedGamesCount / (maxSavedGames || 3)) * 100);
  const playPercentage = Math.min(100, (playedGamesCount / (maxPlayedGames || 3)) * 100);
  
  const isAtSaveLimit = savedGamesCount >= (maxSavedGames || 3);
  const isNearSaveLimit = savedGamesCount >= (maxSavedGames || 3) - 1;
  
  const isAtPlayLimit = playedGamesCount >= (maxPlayedGames || 3);
  const isNearPlayLimit = playedGamesCount >= (maxPlayedGames || 3) - 1;

  if (compact) {
    return (
      <div className="game-limits-compact">
        <div className={`game-limit-compact ${isAtSaveLimit ? 'at-limit' : isNearSaveLimit ? 'near-limit' : ''}`}>
          <Save size={14} />
          <span className="limit-text">{savedGamesCount}/{maxSavedGames || 3}</span>
          <div className="limit-bar-compact">
            <div 
              className="limit-bar-fill-compact" 
              style={{ width: `${savePercentage}%` }}
            />
          </div>
        </div>
        {showPlayLimit && (
          <div className={`game-limit-compact ${isAtPlayLimit ? 'at-limit' : isNearPlayLimit ? 'near-limit' : ''}`}>
            <Gamepad2 size={14} />
            <span className="limit-text">{playedGamesCount}/{maxPlayedGames || 3}</span>
            <div className="limit-bar-compact">
              <div 
                className="limit-bar-fill-compact" 
                style={{ width: `${playPercentage}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="game-limit-indicator-container">
      {/* Save Limit */}
      <div className={`game-limit-indicator ${isAtSaveLimit ? 'at-limit' : isNearSaveLimit ? 'near-limit' : ''}`}>
        <div className="limit-header">
          <div className="limit-label">
            <Save size={16} />
            <span>Games Saved</span>
          </div>
          <span className="limit-count">{savedGamesCount} / {maxSavedGames || 3}</span>
        </div>
        
        <div className="limit-bar">
          <div 
            className="limit-bar-fill" 
            style={{ width: `${savePercentage}%` }}
          />
        </div>

        {isAtSaveLimit ? (
          <div className="limit-message at-limit">
            <span>📚 You've used all your free game slots!</span>
          </div>
        ) : isNearSaveLimit ? (
          <div className="limit-message near-limit">
            <span>⚠️ Only {savesRemaining} game slot{savesRemaining > 1 ? 's' : ''} remaining</span>
          </div>
        ) : (
          <div className="limit-message">
            <span>🎮 {savesRemaining} game slot{savesRemaining > 1 ? 's' : ''} available</span>
          </div>
        )}
      </div>

      {/* Play Limit */}
      {showPlayLimit && (
        <div className={`game-limit-indicator ${isAtPlayLimit ? 'at-limit' : isNearPlayLimit ? 'near-limit' : ''}`}>
          <div className="limit-header">
            <div className="limit-label">
              <Gamepad2 size={16} />
              <span>Games Played</span>
            </div>
            <span className="limit-count">{playedGamesCount} / {maxPlayedGames || 3}</span>
          </div>
          
          <div className="limit-bar">
            <div 
              className="limit-bar-fill" 
              style={{ width: `${playPercentage}%` }}
            />
          </div>

          {isAtPlayLimit ? (
            <div className="limit-message at-limit">
              <span>🎯 You've played all your free games!</span>
            </div>
          ) : isNearPlayLimit ? (
            <div className="limit-message near-limit">
              <span>⚠️ Only {playsRemaining} play{playsRemaining > 1 ? 's' : ''} remaining</span>
            </div>
          ) : (
            <div className="limit-message">
              <span>🎲 {playsRemaining} play{playsRemaining > 1 ? 's' : ''} available</span>
            </div>
          )}
        </div>
      )}

      {/* Upgrade CTA */}
      {(isAtSaveLimit || isAtPlayLimit) && (
        <a href="/profile" className="upgrade-cta">
          <Crown size={16} />
          <span>Upgrade for unlimited games</span>
        </a>
      )}
    </div>
  );
}

export default GameLimitIndicator;
